// 배송지 추가/수정 폼.
// 라우트: /me/addresses/new (생성), /me/addresses/:id/edit (수정)
// 우편번호 API 없이 자유 텍스트 입력만 지원한다 (MVP 단순화).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addressInputSchema,
  isDeliveryAddressAllowed,
  normalizePhoneKr,
} from "@oo/shared";
import { Button } from "@/shared/ui";
import {
  createAddress,
  listAddresses,
  updateAddress,
} from "@/features/operations/operationsApi.ts";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";
import { openPostcodeSearch } from "@/features/operations/daumPostcode.ts";
import { formatPhoneKrInput } from "@/features/auth/formatPhone.ts";

interface FormState {
  label: string;
  recipientName: string;
  recipientPhone: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  isDefault: boolean;
}

const EMPTY: FormState = {
  label: "",
  recipientName: "",
  recipientPhone: "",
  addressLine1: "",
  addressLine2: "",
  postalCode: "",
  isDefault: false,
};

// 배송지 추가/수정 화면.
export default function AddressFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const ops = useStoreSettings();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // 수정 모드면 기존 주소를 불러와 폼을 채운다.
  // 단건 조회 라우트가 별도로 없으므로 목록에서 찾는다.
  const listQuery = useQuery({
    queryKey: ["addresses"],
    queryFn: listAddresses,
    enabled: isEdit,
  });

  useEffect(() => {
    if (isEdit && listQuery.data) {
      const found = listQuery.data.find((a) => a.id === id);
      if (found) {
        setForm({
          label: found.label ?? "",
          recipientName: found.recipientName,
          recipientPhone: formatPhoneKrInput(found.recipientPhone),
          addressLine1: found.addressLine1,
          addressLine2: found.addressLine2 ?? "",
          postalCode: found.postalCode ?? "",
          isDefault: found.isDefault,
        });
      }
    }
  }, [isEdit, listQuery.data, id]);

  const createMut = useMutation({
    mutationFn: createAddress,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["addresses"] });
      navigate("/me/addresses", { replace: true });
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : "주소 추가에 실패했습니다."),
  });
  const updateMut = useMutation({
    mutationFn: (input: Parameters<typeof updateAddress>[1]) => updateAddress(id!, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["addresses"] });
      navigate("/me/addresses", { replace: true });
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : "주소 수정에 실패했습니다."),
  });

  // 도로명 주소 검색 (다음 우편번호). 선택 시 주소/우편번호를 채운다.
  async function onSearchAddress() {
    try {
      const result = await openPostcodeSearch();
      if (result) {
        setForm((f) => ({ ...f, addressLine1: result.roadAddress, postalCode: result.zonecode }));
      }
    } catch {
      setError("주소 검색을 불러오지 못했습니다. 직접 입력해 주세요.");
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input = {
      label: form.label.trim() || null,
      recipientName: form.recipientName.trim(),
      recipientPhone: form.recipientPhone.trim(),
      addressLine1: form.addressLine1.trim(),
      addressLine2: form.addressLine2.trim() || null,
      postalCode: form.postalCode.trim() || null,
      isDefault: form.isDefault,
    };
    const parsed = addressInputSchema.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "입력값을 확인해 주세요.");
      return;
    }
    if (!normalizePhoneKr(parsed.data.recipientPhone)) {
      setError("휴대폰 번호 형식이 올바르지 않습니다.");
      return;
    }
    setError(null);
    if (isEdit) {
      updateMut.mutate(parsed.data);
    } else {
      createMut.mutate(parsed.data);
    }
  }

  // 배달 가능 지역 사전 검사 (제출 차단은 아니고 안내만).
  const fullAddress = `${form.addressLine1} ${form.addressLine2}`.trim();
  const deliveryAllowedCheck =
    ops.settings && ops.settings.deliveryEnabled && fullAddress.length > 0
      ? isDeliveryAddressAllowed(fullAddress, ops.settings.deliveryAreaKeywords)
      : null;

  const pending = createMut.isPending || updateMut.isPending;

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-32">
      <header>
        <h1 className="text-2xl font-bold">{isEdit ? "배송지 수정" : "배송지 추가"}</h1>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <Field label="별칭 (선택)" hint="예: 집, 사무실">
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            maxLength={30}
            className="h-11 rounded-md border border-ink-300 px-3 text-base"
          />
        </Field>
        <Field label="받는 분">
          <input
            type="text"
            value={form.recipientName}
            onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
            maxLength={30}
            required
            className="h-11 rounded-md border border-ink-300 px-3 text-base"
          />
        </Field>
        <Field label="연락처">
          <input
            type="tel"
            value={form.recipientPhone}
            onChange={(e) =>
              setForm({ ...form, recipientPhone: formatPhoneKrInput(e.target.value) })
            }
            required
            placeholder="010-1234-5678"
            className="h-11 rounded-md border border-ink-300 px-3 text-base tabular-nums"
          />
        </Field>
        <Field label="주소">
          <div className="flex gap-2">
            <input
              type="text"
              value={form.addressLine1}
              onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
              maxLength={100}
              required
              placeholder="검색 또는 직접 입력"
              className="h-11 min-w-0 flex-1 rounded-md border border-ink-300 px-3 text-base"
            />
            <button
              type="button"
              onClick={onSearchAddress}
              className="h-11 shrink-0 rounded-md border border-ink-400 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100"
            >
              주소 검색
            </button>
          </div>
        </Field>
        <Field label="상세 주소 (선택)">
          <input
            type="text"
            value={form.addressLine2}
            onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
            maxLength={100}
            placeholder="동/호수 등"
            className="h-11 rounded-md border border-ink-300 px-3 text-base"
          />
        </Field>
        <Field label="우편번호 (선택)">
          <input
            type="text"
            value={form.postalCode}
            onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
            maxLength={10}
            className="h-11 rounded-md border border-ink-300 px-3 text-base tabular-nums"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isDefault}
            onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
          />
          기본 배송지로 지정
        </label>

        {/* 배달 가능 지역 안내 (선택사항) */}
        {deliveryAllowedCheck === false ? (
          <p className="rounded-md border border-warning-600/30 bg-warning-50 p-2 text-xs text-warning-700">
            입력하신 주소는 현재 등록된 배달 가능 지역에 포함되지 않을 수 있습니다. 매장에 문의해 주세요.
          </p>
        ) : null}

        {error ? (
          <div className="rounded-md border border-danger-600/30 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </div>
        ) : null}

        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            취소
          </Button>
          <Button type="submit" loading={pending} className="flex-1">
            {pending ? "저장 중..." : "저장"}
          </Button>
        </div>
      </form>
    </main>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium text-ink-700">{label}</span>
      {children}
      {hint ? <span className="text-xs text-ink-600">{hint}</span> : null}
    </label>
  );
}
